
import React, { useState } from 'react';
import { useGuitarMatch, SAFE_GAIN_MIN } from '../../context/GuitarMatchContext';
import { TimeSignature, SoundProfile } from '../../types';

interface StudioConsoleProps {
    lang: 'en' | 'fr';
    bpm: number;
    setBpm: (b: number) => void;
    timeSignature: TimeSignature;
    setTimeSignature: (s: TimeSignature) => void;
    micGain: number;
    setMicGain: (g: number) => void;
    metronomeVolume: number;
    setMetronomeVolume: (v: number) => void;
    teacherVolume: number;
    setTeacherVolume: (v: number) => void;
    vuLevel: number;
    selectedDeviceId: string;
    audioDevices: MediaDeviceInfo[];
    onDeviceChange: (id: string) => void;
    selectedVideoId?: string;
    videoDevices?: MediaDeviceInfo[];
    onVideoDeviceChange?: (id: string) => void;
    offset: number;
    setOffset: (o: number) => void;
    isVisible: boolean;
    onClose: () => void;
    isCollapsed: boolean;
    onToggleCollapse: () => void;
    onRecalibrate?: () => void;
    isLockedBySystem?: boolean; 
}

export const StudioConsole: React.FC<StudioConsoleProps> = (props) => {
    const { micGain, setMicGain, soundProfile, setSoundProfile } = useGuitarMatch();
    const [directMonitor, setDirectMonitor] = useState(false);
    
    const isSignalPresent = props.vuLevel > 0.002;
    const isFr = props.lang === 'fr';
    const isBusy = props.isLockedBySystem;
    
    const gainDb = micGain > 0 ? (20 * Math.log10(micGain)).toFixed(1) : "-inf";

    return (
        <aside 
            className={`fixed md:relative h-full bg-[#00141a]/98 md:bg-[#00141a]/95 backdrop-blur-3xl border-r border-white/10 flex flex-col z-[5000] transition-all duration-500 ease-in-out shadow-2xl shrink-0 pt-[max(env(safe-area-inset-top),_20px)] ${
                props.isCollapsed 
                ? 'w-0 md:w-14 -translate-x-full md:translate-x-0' 
                : 'w-[90vw] sm:w-80 translate-x-0'
            }`}
        >
            <button 
                id="studio-console-toggle-btn"
                onClick={props.onToggleCollapse}
                className={`fixed md:absolute top-1/2 -translate-y-1/2 w-10 h-24 md:w-10 md:h-32 bg-smart-accent rounded-r-2xl md:rounded-full flex items-center justify-center z-[5100] text-white shadow-[0_0_30px_rgba(255,98,97,0.3)] hover:scale-110 transition-all ${
                    props.isCollapsed ? 'left-0 md:right-[-20px]' : 'left-[90vw] sm:left-80 md:right-[-20px]'
                }`}
                aria-label={props.isCollapsed ? "Open settings" : "Close settings"}
            >
                <span className={`text-lg font-black transition-transform ${props.isCollapsed ? '' : 'rotate-180'}`}>❯</span>
            </button>

            <div className={`flex flex-col h-full p-6 md:p-8 transition-opacity duration-300 overflow-hidden ${props.isCollapsed ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
                <div className="flex justify-between items-center mb-8 shrink-0">
                    <h2 className="text-xl md:text-2xl font-black italic text-white uppercase tracking-tighter flex items-center gap-3">
                        <span className="w-1.5 h-8 bg-smart-accent rounded-full animate-pulse"></span>
                        Console
                    </h2>
                </div>

                <div className={`flex-1 space-y-8 md:space-y-10 overflow-y-auto custom-scrollbar pb-10 pr-2`}>
                    
                    <div className="space-y-4">
                        <button 
                            id="studio-calib-btn"
                            onClick={props.onRecalibrate} 
                            disabled={isBusy}
                            className="w-full py-6 rounded-[24px] bg-white text-black text-[11px] font-black uppercase tracking-[0.2em] hover:bg-cyan-400 transition-all shadow-[0_0_30px_rgba(0,0,0,0.4)] flex items-center justify-center gap-3 border-2 border-transparent active:scale-95 disabled:opacity-30"
                        >
                            <span className="text-lg">⚡</span> {isFr ? 'OPTIMISER STUDIO' : 'OPTIMIZE STUDIO'}
                        </button>
                        
                        <div className="flex justify-between items-center px-4">
                            <span className="text-[8px] font-black text-slate-500 uppercase tracking-widest italic">Latence actuelle</span>
                            <span className="text-[10px] font-mono font-black text-cyan-400">{props.offset.toFixed(1)} ms</span>
                        </div>
                    </div>

                    <div id="studio-vu-meter" className="bg-black/60 p-5 rounded-[32px] border border-white/10 space-y-4 shadow-inner">
                        <div className="flex justify-between items-center px-1">
                            <span className="text-[9px] font-black text-slate-500 uppercase tracking-widest italic">Input Gain</span>
                            <button 
                                onClick={() => setDirectMonitor(!directMonitor)}
                                className={`text-[8px] font-black uppercase px-2 py-0.5 rounded-full border transition-all ${directMonitor ? 'bg-emerald-500 border-emerald-500 text-black' : 'text-slate-500 border-white/10'}`}
                            >
                                {directMonitor ? 'MONITOR ON' : 'MONITOR OFF'}
                            </button>
                        </div>
                        <div className="h-3 w-full bg-black/80 rounded-full border border-white/5 overflow-hidden flex gap-0.5 p-0.5">
                            {Array.from({length: 15}).map((_, i) => (
                                <div key={i} className={`flex-1 rounded-[1px] transition-all duration-75 ${i/15 < props.vuLevel ? (i > 12 ? 'bg-red-500 shadow-[0_0_10px_red]' : i > 10 ? 'bg-smart-gold' : 'bg-cyan-500') : 'bg-white/5'}`} />
                            ))}
                        </div>
                        {directMonitor && (
                             <p className="text-[7px] text-orange-400 italic text-center px-2 leading-tight">
                                {isFr ? "Attention : Le monitoring logiciel peut créer un écho. Privilégiez l'écoute directe." : "Warning: Software monitoring can cause echo. Direct monitoring preferred."}
                             </p>
                        )}
                    </div>

                    <div className={`space-y-8 transition-opacity ${isBusy ? 'opacity-30 pointer-events-none' : 'opacity-100'}`}>
                        <div className="space-y-4">
                            <div className="flex justify-between items-center px-1">
                                <label className="text-[9px] font-black text-white uppercase tracking-widest flex items-center gap-2"><span>🔊</span> {isFr ? 'Volume Master' : 'Master Volume'}</label>
                                <span className="text-[10px] font-mono font-bold text-slate-400">{Math.round(props.teacherVolume * 100)}%</span>
                            </div>
                            <input 
                                type="range" min="0" max="1" step="0.05" 
                                value={props.teacherVolume} 
                                onChange={e => props.setTeacherVolume(parseFloat(e.target.value))} 
                                className="w-full h-1.5 accent-smart-accent bg-white/10 rounded-full appearance-none cursor-pointer" 
                            />
                        </div>

                        <div className="space-y-4">
                            <div className="flex justify-between items-center px-1">
                                <label className="text-[9px] font-black text-cyan-400 uppercase tracking-widest italic">IA Pre-Gain</label>
                                <span className="text-[10px] font-mono font-bold text-white bg-white/5 px-2 py-0.5 rounded">+{gainDb}dB</span>
                            </div>
                            <input type="range" min={SAFE_GAIN_MIN} max={10} step="0.1" value={micGain} onChange={e => setMicGain(parseFloat(e.target.value))} className="w-full h-1.5 accent-cyan-400 bg-white/10 rounded-full appearance-none cursor-pointer" />
                        </div>

                        <div className="space-y-2">
                            <label className="text-[8px] font-black text-slate-500 uppercase tracking-widest ml-1 italic">Audio Source</label>
                            <select 
                                value={props.selectedDeviceId}
                                onChange={(e) => props.onDeviceChange(e.target.value)}
                                className="w-full bg-black/60 border border-white/10 rounded-2xl px-4 py-4 text-[10px] font-bold text-white outline-none focus:border-smart-accent appearance-none cursor-pointer"
                            >
                                {props.audioDevices.map(device => (
                                    <option key={device.deviceId} value={device.deviceId}>{device.label || `Device ${device.deviceId.slice(0,5)}`}</option>
                                ))}
                            </select>
                        </div>
                    </div>

                </div>

                <div className="mt-auto pt-6 border-t border-white/5 flex flex-col gap-3 pb-[env(safe-area-inset-bottom)]">
                    <button 
                        onClick={props.onClose} 
                        className="py-6 w-full rounded-[24px] bg-white text-black font-black uppercase text-[11px] tracking-widest hover:bg-smart-accent hover:text-white transition-all shadow-2xl"
                    >
                        {isFr ? 'RETOUR STUDIO' : 'BACK TO STUDIO'}
                    </button>
                </div>
            </div>
        </aside>
    );
};
